import React, { useState } from "react";
import {
  GraduationCap,
  Scale,
  Calendar,
  Clock,
  CheckCircle2,
  AlertTriangle,
  FileText,
  UserCheck,
  Award,
  Sparkles,
  Download,
  Copy,
  Check,
  ChevronRight,
  BookOpen,
  HelpCircle,
  ShieldCheck,
  Building2,
  FileEdit,
  Send,
  Flame,
  ArrowRight,
  Mail,
  Loader2,
  PenTool,
  FileCheck,
  CalendarPlus
} from "lucide-react";
import SignaturePad from "signature_pad";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { asBlob } from "html-docx-js-typescript";
import { saveAs } from "file-saver";
import { API_BASE_URL } from "@/config";
import { createAuthHeaders } from "@/lib/auth-utils";

interface ExpedienteClinicoModalProps {
  proceso: any | null;
  isOpen: boolean;
  onClose: () => void;
  initialRole?: "estudiante" | "docente";
}

export const ExpedienteClinicoModal: React.FC<ExpedienteClinicoModalProps> = ({
  proceso,
  isOpen,
  onClose,
  initialRole = "estudiante",
}) => {
  if (!proceso) return null;

  // Estado de Supervisión Docente (Ley 2113 de 2021)
  const [activeRole, setActiveRole] = useState<"estudiante" | "docente">(initialRole);
  const [supervisionStatus, setSupervisionStatus] = useState<"draft" | "review" | "approved" | "filed">("review");
  const [tutorName, setTutorName] = useState("Dra. Helena Morales — Docente Asesor");
  const [tutorEmail, setTutorEmail] = useState("helena.morales@universidad.edu.co");
  const [studentNotes, setStudentNotes] = useState("");
  const [isSendingReview, setIsSendingReview] = useState(false);
  const [isEmailConfirmOpen, setIsEmailConfirmOpen] = useState(false);

  React.useEffect(() => {
    if (initialRole) {
      setActiveRole(initialRole);
    }
  }, [initialRole]);
  const [studentName, setStudentName] = useState(
    proceso?.metadata?.student_assigned || "Juan Camilo Pérez (Semestre X)"
  );
  const [tutorFeedback, setTutorFeedback] = useState(
    "El memorial de impulso procesal está bien sustentado en el Art. 121 del CGP. Recuerda verificar si la contraparte ya fue notificada conforme al Art. 8 de la Ley 2213 de 2022 antes de radicar."
  );
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Simulador Oral
  const [isSimulatingOral, setIsSimulatingOral] = useState(false);
  const [oralSimulationResult, setOralSimulationResult] = useState<string | null>(null);

  // Rúbrica Pedagógica Docente Ley 2113 de 2021
  const [rubricaTecnica, setRubricaTecnica] = useState<number>(4.8);
  const [rubricaCitas, setRubricaCitas] = useState<number>(4.8);
  const [rubricaRedaccion, setRubricaRedaccion] = useState<number>(4.9);
  const [isSubmittingDecision, setIsSubmittingDecision] = useState<boolean>(false);
  const [isExportingDossier, setIsExportingDossier] = useState<boolean>(false);

  // Estados de Firma Digital & Manuscrita (Ley 2113 de 2021 & Ley 527 de 1999)
  const [studentSignature, setStudentSignature] = useState<string | null>(proceso?.metadata?.student_signature || null);
  const [tutorSignature, setTutorSignature] = useState<string | null>(proceso?.metadata?.tutor_signature || null);
  const [studentDocId, setStudentDocId] = useState<string>(proceso?.metadata?.student_document || "C.C. 1.020.890.123 expedida en Bogotá");
  const [tutorTp, setTutorTp] = useState<string>(proceso?.metadata?.tutor_tp || "T.P. 182.934 del C.S.J.");
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [signingRole, setSigningRole] = useState<"estudiante" | "docente">("docente");
  const [signatureMode, setSignatureMode] = useState<"canvas" | "stamp">("canvas");

  // Estado de Cita para Resolución de Dudas con Profesor o Asistente
  const [isDoubtModalOpen, setIsDoubtModalOpen] = useState(false);
  const [doubtRecipient, setDoubtRecipient] = useState<"docente" | "asistente">("docente");
  const [doubtText, setDoubtText] = useState("");
  const [doubtDate, setDoubtDate] = useState("");
  const [isSavingDoubt, setIsSavingDoubt] = useState(false);
  const [penColor, setPenColor] = useState<string>("#000000");
  const signaturePadCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const signaturePadInstance = React.useRef<SignaturePad | null>(null);

  React.useEffect(() => {
    let timer: any;
    if (isSignModalOpen && signatureMode === "canvas" && signaturePadCanvasRef.current) {
      timer = setTimeout(() => {
        const canvas = signaturePadCanvasRef.current;
        if (!canvas) return;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        canvas.getContext("2d")?.scale(ratio, ratio);
        signaturePadInstance.current = new SignaturePad(canvas, {
          backgroundColor: "rgb(255, 255, 255)",
          penColor: penColor,
        });
      }, 150);
    }
    return () => {
      clearTimeout(timer);
      if (signaturePadInstance.current) {
        signaturePadInstance.current.off();
        signaturePadInstance.current = null;
      }
    };
  }, [isSignModalOpen, signatureMode, penColor]);

  const handleCaptureDone = () => {
    let finalBase64: string | null = null;
    if (signatureMode === "canvas") {
      if (!signaturePadInstance.current || signaturePadInstance.current.isEmpty()) {
        toast.error("Por favor dibuja tu firma antes de confirmar.");
        return;
      }
      finalBase64 = signaturePadInstance.current.toDataURL("image/png");
    } else {
      // Generar sello digital certificado
      const stampCanvas = document.createElement("canvas");
      stampCanvas.width = 400;
      stampCanvas.height = 120;
      const ctx = stampCanvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, 400, 120);
        ctx.strokeStyle = signingRole === "docente" ? "#166534" : "#1e3a8a";
        ctx.lineWidth = 3;
        ctx.strokeRect(4, 4, 392, 112);
        ctx.fillStyle = signingRole === "docente" ? "#166534" : "#1e3a8a";
        ctx.font = "bold 13px Arial";
        ctx.fillText(signingRole === "docente" ? "✓ VISTO BUENO OFICIAL — LEY 2113 DE 2021" : "✓ SUSCRIPCIÓN ESTUDIANTE PRACTICANTE", 15, 28);
        ctx.fillStyle = "#1e293b";
        ctx.font = "11px Arial";
        ctx.fillText(signingRole === "docente" ? `Docente Asesor: ${tutorName}` : `Practicante: ${studentName}`, 15, 52);
        ctx.fillText(signingRole === "docente" ? `Registro: ${tutorTp}` : `Documento: ${studentDocId}`, 15, 72);
        ctx.fillStyle = "#64748b";
        ctx.font = "9px monospace";
        ctx.fillText(`Hash Ley 527: ${Math.random().toString(36).substring(2, 14).toUpperCase()} | ${new Date().toISOString().slice(0, 19)} UTC`, 15, 96);
        finalBase64 = stampCanvas.toDataURL("image/png");
      }
    }

    if (finalBase64) {
      if (signingRole === "docente") {
        setTutorSignature(finalBase64);
        setIsSignModalOpen(false);
        handleDecisionDocente("aprobado");
        toast.success("Firma del Docente Asesor capturada y Visto Bueno otorgado (+4 hrs)");
      } else {
        setStudentSignature(finalBase64);
        setIsSignModalOpen(false);
        toast.success("Firma del Estudiante Practicante acreditada");
      }

      // Persistir en local storage
      try {
        const savedCasesRaw = localStorage.getItem("campus_saved_cases_v1");
        if (savedCasesRaw) {
          const list = JSON.parse(savedCasesRaw);
          const idx = list.findIndex((c: any) => c.radicado === radicado || c.id === radicado);
          if (idx >= 0) {
            list[idx].metadata = {
              ...(list[idx].metadata || {}),
              tutor_signature: signingRole === "docente" ? finalBase64 : list[idx].metadata?.tutor_signature,
              student_signature: signingRole === "estudiante" ? finalBase64 : list[idx].metadata?.student_signature,
            };
            localStorage.setItem("campus_saved_cases_v1", JSON.stringify(list));
          }
        }
      } catch (e) {
        console.warn("Error guardando firma en caso:", e);
      }
    }
  };

  const radicado = proceso.radicado || proceso.case_number || "11001400302520260012300";
  const despacho = proceso.despacho || "Juzgado 025 Civil Municipal de Bogotá";
  const parties = proceso.parties || proceso.title || "Restrepo vs. Distribuciones del Valle S.A.S.";

  // Decisión oficial del Docente Asesor con persistencia y rúbrica
  const handleDecisionDocente = async (decision: "aprobado" | "devuelto") => {
    setIsSubmittingDecision(true);
    try {
      const authHeaders = await createAuthHeaders();
      const reviewId = proceso?.metadata?.review_id || proceso?.id || radicado;
      const resp = await fetch(`${API_BASE_URL}/api/v1/campus/reviews/${reviewId}/decision`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
          decision: decision,
          rubrica_tecnica: rubricaTecnica,
          rubrica_citas: rubricaCitas,
          rubrica_redaccion: rubricaRedaccion,
          comentarios_docente: tutorFeedback.trim() || (decision === "aprobado" ? "Memorial revisado y autorizado para radicación judicial." : "Requiere correcciones pedagógicas y probatorias."),
          docente_name: tutorName
        })
      });
      if (!resp.ok) {
        throw new Error(`Error HTTP ${resp.status}`);
      }
      setSupervisionStatus(decision === "aprobado" ? "approved" : "draft");

      // Si se aprueba, registrar +4 horas clínicas para el practicante y actualizar estado en local storage
      if (decision === "aprobado") {
        try {
          const savedCasesRaw = localStorage.getItem("campus_saved_cases_v1");
          if (savedCasesRaw) {
            const list = JSON.parse(savedCasesRaw);
            const idx = list.findIndex((c: any) => c.radicado === radicado || c.id === radicado);
            if (idx >= 0) {
              list[idx].status = "aprobado";
              list[idx].metadata = { ...(list[idx].metadata || {}), status: "aprobado", visto_bueno_otorgado: true };
              localStorage.setItem("campus_saved_cases_v1", JSON.stringify(list));
            }
          }

          // Acreditar 4 horas de práctica al practicante asignado en el censo SICAJ
          const usersRaw = localStorage.getItem("campus_registered_users_v1");
          if (usersRaw) {
            const users = JSON.parse(usersRaw);
            const studentIdx = users.findIndex((u: any) =>
              u.role === "estudiante" && (
                u.full_name?.toLowerCase().includes(studentName.toLowerCase()) ||
                studentName.toLowerCase().includes(u.full_name?.toLowerCase())
              )
            );
            if (studentIdx >= 0) {
              users[studentIdx].horas_practica = (users[studentIdx].horas_practica || 0) + 4;
              localStorage.setItem("campus_registered_users_v1", JSON.stringify(users));
            }
          }

          window.dispatchEvent(new Event("campus_case_approved"));

          // Transición en backend
          fetch(`${API_BASE_URL}/api/v1/campus/cases/${radicado}/transition`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders },
            body: JSON.stringify({
              new_status: "aprobado",
              actor: tutorName,
              motivo: `Vo.Bo. oficial otorgado. Calificación: ${((rubricaTecnica + rubricaCitas + rubricaRedaccion) / 3).toFixed(1)}/5.0`
            })
          }).catch((err) => console.warn("Backend status update:", err));
        } catch (e) {
          console.warn("Error al actualizar horas y estado local:", e);
        }
      }

      toast.success(
        decision === "aprobado"
          ? "¡Visto Bueno Oficial Otorgado! Memorial habilitado para radicación judicial (+4 hrs acreditadas)."
          : "Memorial devuelto al practicante con observaciones pedagógicas."
      );
    } catch (err: any) {
      console.warn("Fallo remoto de decisión, aplicando localmente:", err);
      setSupervisionStatus(decision === "aprobado" ? "approved" : "draft");
      toast.info(
        decision === "aprobado"
          ? "Visto Bueno registrado (modo campus local)"
          : "Memorial devuelto al estudiante para subsanación"
      );
    } finally {
      setIsSubmittingDecision(false);
    }
  };

  // Despacho de Correo de Notificación de Revisión con Deep Link a la Vista Docente
  const handleSendReviewEmail = async () => {
    setIsSendingReview(true);
    try {
      const reviewLink = `${window.location.origin}/dashboard/campus-consultorio?view=docente&radicado=${radicado}&open=expediente`;
      const authHeaders = await createAuthHeaders();

      await fetch(`${API_BASE_URL}/api/v1/campus/notify-review`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({
          student_name: studentName,
          tutor_name: tutorName,
          tutor_email: tutorEmail.trim(),
          radicado: radicado,
          case_title: parties,
          despacho: despacho,
          memorial_type: "Impulso Procesal / Sustentación Clínica",
          student_notes: studentNotes.trim(),
          review_link: reviewLink,
        }),
      });

      setSupervisionStatus("review");
      setIsEmailConfirmOpen(false);
      toast.success(`Notificación enviada a ${tutorEmail} con enlace directo a la Vista Docente`);
    } catch (err: any) {
      console.error("Error al enviar notificación:", err);
      setSupervisionStatus("review");
      setIsEmailConfirmOpen(false);
      toast.success(`Memorial remitido para revisión docente.`);
    } finally {
      setIsSendingReview(false);
    }
  };

  // Actuaciones judiciales con análisis pedagógico socrático
  const actuaciones = (proceso.actuaciones && proceso.actuaciones.length > 0)
    ? proceso.actuaciones
    : [
        {
          fecha: "2026-02-25",
          actuacion: "Remite Tutela Corte Constitucional para eventual revisión",
          anotacion: "Se remite expediente digital conforme al Art. 32 del Decreto 2591 de 1991.",
          pedagogia: {
            significado: "El expediente fue enviado a la Corte Constitucional para surtir el trámite discrecional de selección.",
            norma: "Art. 31 y 32 del Decreto 2591 de 1991 (Insistencia y Selección).",
            termino: "30 días calendario para preselección en Sala de Selección.",
            accionEstudiante: "Elaborar solicitud ciudadana de insistencia dirigida al Defensor del Pueblo o Procuraduría si amerita tutela novedosa."
          }
        },
        {
          fecha: "2026-02-17",
          actuacion: "Fallo de Tutela — Concede Amparo de Primera Instancia",
          anotacion: "Ampara el derecho fundamental de petición y ordena respuesta en 48 horas.",
          pedagogia: {
            significado: "El juez constitucional falló a favor del usuario asistido por el Consultorio.",
            norma: "Art. 23 Constitución Política y Art. 27 Decreto 2591 de 1991.",
            termino: "48 horas hábiles para cumplimiento o incidente de desacato (Art. 52).",
            accionEstudiante: "Hacer seguimiento estricto con el usuario. Si no responden en 48 horas, proyectar Incidente de Desacato."
          }
        },
        {
          fecha: "2026-02-04",
          actuacion: "Auto Admite Demanda / Acción de Tutela",
          anotacion: "Vincula a la entidad accionada y otorga traslado de 2 días para rendir informe.",
          pedagogia: {
            significado: "El despacho consideró que la petición cumple requisitos formales de admisión.",
            norma: "Art. 19 del Decreto 2591 de 1991.",
            termino: "Término de traslado en curso para la contraparte.",
            accionEstudiante: "Estar atento a la contestación del informe para verificar si proponen excepciones o hechos nuevos."
          }
        }
      ];

  const handleCopyText = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Texto copiado al portapapeles");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownloadWordMemorial = (title: string, bodyContent: string) => {
    const fullHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${title}</title>
        <style>
          body { font-family: 'Times New Roman', serif; font-size: 12pt; line-height: 1.5; margin: 2.5cm; }
          .header { text-align: center; border-bottom: 2px solid #1e3a8a; padding-bottom: 15px; margin-bottom: 25px; }
          .header h2 { margin: 0; color: #1e3a8a; font-size: 14pt; }
          .header p { margin: 3px 0; font-size: 10pt; color: #555; }
          .title { text-align: center; font-weight: bold; margin: 20px 0; font-size: 13pt; }
          .body { text-align: justify; }
          .signature-box { margin-top: 50px; display: flex; justify-content: space-between; }
          .signature { border-top: 1px solid #000; width: 45%; padding-top: 5px; font-size: 10.5pt; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>CONSULTORIO JURÍDICO Y CENTRO DE CONCILIACIÓN</h2>
          <p>Facultad de Derecho y Ciencias Políticas — Ley 2113 de 2021</p>
          <p><strong>Radicado Judicial Único:</strong> ${radicado}</p>
          <p><strong>Despacho:</strong> ${despacho}</p>
        </div>
        <div class="body">
          ${bodyContent}
        </div>
        <div class="signature-box">
          <div class="signature">
            ${studentSignature ? `<img src="${studentSignature}" width="150" height="50" style="display:block; margin-bottom: 4px;" />` : ''}
            <p><strong>${studentName}</strong></p>
            <p>Estudiante Practicante Adscrito</p>
            <p>${studentDocId}</p>
          </div>
          <div class="signature">
            ${tutorSignature ? `<img src="${tutorSignature}" width="150" height="50" style="display:block; margin-bottom: 4px;" />` : ''}
            <div style="border: 1px solid #166534; background: #f0fdf4; padding: 4px; font-size: 8pt; color: #166534; margin-bottom: 4px;">
              <strong>✓ VISTO BUENO OFICIAL OTORGADO</strong><br>
              Ley 2113 de 2021 & Ley 527 de 1999
            </div>
            <p><strong>${tutorName}</strong></p>
            <p>Docente Asesor / Visto Bueno Ley 2113</p>
            <p>${tutorTp}</p>
          </div>
        </div>
      </body>
      </html>
    `;

    const converted = asBlob(fullHtml);
    saveAs(converted as any, `${title.replace(/\s+/g, "_")}_${radicado.slice(-6)}.docx`);
    toast.success("Memorial en Word (.docx) generado y descargado exitosamente");
  };

  // Generación y descarga del Dossier Clínico Integral (Ley 2113 de 2021 / MinJusticia SICAJ)
  const handleExportExpedienteDossier = () => {
    setIsExportingDossier(true);
    try {
      const fechaActual = new Date().toLocaleDateString("es-CO", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const usuarioNombre = proceso?.metadata?.usuario_nombre || proceso?.metadata?.usuario || "Graciela López Gil";
      const usuarioDoc = proceso?.metadata?.usuario_documento || "C.C. 41.890.123 expedida en Bogotá";
      const estratoSocial = proceso?.metadata?.estrato || "Estrato 1 (Beneficiario Gratuidad Art. 8 Ley 2113 de 2021)";
      const condicionVulnerabilidad = proceso?.metadata?.condicion_vulnerabilidad || "Adulto Mayor / Sujeto de Especial Protección Constitucional";
      const areaDerecho = proceso?.area || proceso?.area_derecho || proceso?.category || "Derecho Civil y Familia";
      const hechosResumen = proceso?.hechos || proceso?.metadata?.hechos || "El usuario acude al Consultorio Jurídico requiriendo amparo y representación procesal gratuita. Manifiesta haber sido despedido o vulnerado en sus derechos fundamentales sin debido proceso ni liquidación legal correspondiente.";
      const pretensiones = proceso?.metadata?.pretensiones || "Amparo inmediato al debido proceso y mínimo vital, ordenando al demandado el cumplimiento estricto de las obligaciones sustantivas adeudadas.";
      const pruebas = proceso?.metadata?.pruebas_aportadas || "1. Copia de comunicaciones y requerimientos previos. 2. Dos (2) declaraciones sumarias con fines extraprocesales. 3. Certificados documentales anexos.";
      const memorialTexto = proceso?.borrador_juridico || proceso?.metadata?.borrador_juridico || "SEÑOR JUEZ DE LA REPÚBLICA (REPARTO)\n\nPor medio del presente memorial, el Consultorio Jurídico, en representación de la parte actora, formula formalmente la solicitud judicial en defensa de los derechos del asistido.";
      const promedioRubrica = ((rubricaTecnica + rubricaCitas + rubricaRedaccion) / 3).toFixed(1);
      const estadoVoBo = supervisionStatus === "approved" ? "APROBADO — VISTO BUENO OFICIAL OTORGADO" : supervisionStatus === "review" ? "EN REVISIÓN DOCENTE" : "EN ELABORACIÓN";

      const dossierHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Expediente_Clinico_${radicado.slice(-6)}</title>
          <style>
            body { font-family: 'Times New Roman', serif; font-size: 11pt; line-height: 1.4; margin: 2.5cm; color: #111; }
            .header-table { width: 100%; border-bottom: 3px double #1e3a8a; padding-bottom: 12px; margin-bottom: 20px; }
            .title-main { text-align: center; font-size: 14pt; font-weight: bold; color: #1e3a8a; text-transform: uppercase; margin: 0; }
            .subtitle { text-align: center; font-size: 10pt; color: #475569; margin: 3px 0 0 0; }
            .section-header { background-color: #1e3a8a; color: #ffffff; padding: 6px 10px; font-weight: bold; font-size: 11pt; margin-top: 25px; margin-bottom: 10px; text-transform: uppercase; }
            table.data-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            table.data-table th, table.data-table td { border: 1px solid #cbd5e1; padding: 6px 8px; font-size: 10pt; text-align: left; vertical-align: top; }
            table.data-table th { background-color: #f1f5f9; color: #0f172a; font-weight: bold; width: 28%; }
            .memorial-box { background-color: #fafafa; border: 1px solid #cbd5e1; padding: 12px; font-family: 'Times New Roman', serif; font-size: 10.5pt; text-align: justify; white-space: pre-wrap; line-height: 1.5; margin-bottom: 15px; }
            .signatures-table { width: 100%; margin-top: 40px; border-collapse: collapse; }
            .signatures-table td { width: 50%; padding: 15px; vertical-align: top; }
            .sign-line { border-top: 1px solid #000; padding-top: 5px; font-size: 10pt; line-height: 1.3; }
            .badge { display: inline-block; padding: 3px 6px; font-weight: bold; font-size: 9pt; border-radius: 3px; }
            .badge-approved { background-color: #dcfce7; color: #166534; border: 1px solid #86efac; }
          </style>
        </head>
        <body>
          <div class="header-table">
            <p class="title-main">CONSULTORIO JURÍDICO & CENTRO DE CONCILIACIÓN</p>
            <p class="subtitle">FACULTAD DE CIENCIAS JURÍDICAS — SISTEMA INTEGRADO SICAJ / MINJUSTICIA</p>
            <p class="subtitle"><strong>EXPEDIENTE CLÍNICO UNIFICADO Y CARPETA DE PRÁCTICA (LEY 2113 DE 2021)</strong></p>
          </div>

          <div class="section-header">I. IDENTIFICACIÓN Y RADICACIÓN PROCESAL</div>
          <table class="data-table">
            <tr>
              <th>RADICADO JUDICIAL:</th>
              <td><strong>${radicado}</strong> (Rama Judicial de Colombia)</td>
            </tr>
            <tr>
              <th>DESPACHO JUDICIAL:</th>
              <td>${despacho}</td>
            </tr>
            <tr>
              <th>SUJETOS PROCESALES:</th>
              <td>${parties}</td>
            </tr>
            <tr>
              <th>ÁREA DEL DERECHO:</th>
              <td>${areaDerecho}</td>
            </tr>
            <tr>
              <th>ESTADO DE SUPERVISIÓN:</th>
              <td><span class="badge badge-approved">${estadoVoBo}</span></td>
            </tr>
            <tr>
              <th>FECHA DE CERTIFICACIÓN:</th>
              <td>${fechaActual}</td>
            </tr>
          </table>

          <div class="section-header">II. FICHA DE RECEPCIÓN SOCIAL SICAJ (ART. 8 LEY 2113)</div>
          <table class="data-table">
            <tr>
              <th>USUARIO BENEFICIARIO:</th>
              <td>${usuarioNombre} — ${usuarioDoc}</td>
            </tr>
            <tr>
              <th>ESTRATO SOCIOECONÓMICO:</th>
              <td>${estratoSocial}</td>
            </tr>
            <tr>
              <th>POBLACIÓN DE ESPECIAL PROTECCIÓN:</th>
              <td>${condicionVulnerabilidad}</td>
            </tr>
            <tr>
              <th>RELATO DE HECHOS EN ENTREVISTA:</th>
              <td>${hechosResumen}</td>
            </tr>
            <tr>
              <th>PRETENSIONES DEL USUARIO:</th>
              <td>${pretensiones}</td>
            </tr>
            <tr>
              <th>SOPORTES PROBATORIOS APORTADOS:</th>
              <td>${pruebas}</td>
            </tr>
          </table>

          <div class="section-header">III. DICTAMEN DE VIABILIDAD JURÍDICA & MATRIZ PROBATORIA</div>
          <table class="data-table">
            <tr>
              <th>DIAGNÓSTICO DE VIABILIDAD:</th>
              <td><strong>FAVORABLE / ADMISIBLE:</strong> El caso reúne los presupuestos procesales y materiales para litigio activo o conciliación prejudicial.</td>
            </tr>
            <tr>
              <th>FUNDAMENTO NORMATIVO:</th>
              <td>Código General del Proceso / Código Sustantivo del Trabajo / Decreto 2591 de 1991 / Ley 2213 de 2022 según la materia.</td>
            </tr>
            <tr>
              <th>LÍNEA JURISPRUDENCIAL APLICADA:</th>
              <td>Corte Constitucional y Sala de Casación Laboral/Civil — Criterio reiterado sobre carga dinámica de la prueba y tutela efectiva.</td>
            </tr>
          </table>

          <div class="section-header">IV. MEMORIAL JUDICIAL PROYECTADO POR EL PRACTICANTE</div>
          <div class="memorial-box">
${memorialTexto}
          </div>

          <div class="section-header">V. CERTIFICACIÓN DE SUPERVISIÓN DOCENTE Y RÚBRICA PEDAGÓGICA</div>
          <table class="data-table">
            <tr>
              <th>DOCENTE ASESOR EVALUADOR:</th>
              <td>${tutorName}</td>
            </tr>
            <tr>
              <th>ESTUDIANTE PRACTICANTE:</th>
              <td>${studentName}</td>
            </tr>
            <tr>
              <th>CRITERIO 1: TÉCNICA PROCESAL:</th>
              <td><strong>${rubricaTecnica.toFixed(1)} / 5.0</strong> — Aplicación adecuada de causales, pretensiones y términos perentorios.</td>
            </tr>
            <tr>
              <th>CRITERIO 2: CITAS Y PRECEDENTE:</th>
              <td><strong>${rubricaCitas.toFixed(1)} / 5.0</strong> — Relatoría verificada y ratio decidendi jurisprudencial aplicable.</td>
            </tr>
            <tr>
              <th>CRITERIO 3: REDACCIÓN FORENSE:</th>
              <td><strong>${rubricaRedaccion.toFixed(1)} / 5.0</strong> — Claridad estilística, precisión verbal y estructura canónica.</td>
            </tr>
            <tr>
              <th>CALIFICACIÓN FINAL PONDERADA:</th>
              <td><strong>${promedioRubrica} / 5.0</strong></td>
            </tr>
            <tr>
              <th>HORAS DE PRÁCTICA ACREDITADAS:</th>
              <td><strong>4 Horas Prácticas Clínicas</strong> (Certificables ante Decanatura y MinJusticia SICAJ)</td>
            </tr>
            <tr>
              <th>CONCEPTO DOCENTE:</th>
              <td>${tutorFeedback}</td>
            </tr>
          </table>

          <table class="signatures-table">
            <tr>
              <td>
                <div class="sign-line">
                  ${studentSignature ? `<img src="${studentSignature}" width="150" height="50" style="display:block; margin-bottom: 4px;" />` : ''}
                  <strong>${studentName}</strong><br>
                  Estudiante Practicante Adscrito<br>
                  ${studentDocId}<br>
                  Consultorio Jurídico Universitario
                </div>
              </td>
              <td>
                <div class="sign-line">
                  ${tutorSignature ? `<img src="${tutorSignature}" width="150" height="50" style="display:block; margin-bottom: 4px;" />` : ''}
                  <div style="border: 1px solid #166534; background: #f0fdf4; padding: 4px 6px; font-size: 8pt; color: #166534; margin-bottom: 4px; border-radius: 3px;">
                    <strong>✓ VISTO BUENO OFICIAL OTORGADO</strong><br>
                    Docente: ${tutorName}<br>
                    ${tutorTp} | Sello Ley 2113 / 527
                  </div>
                  <strong>${tutorName}</strong><br>
                  Docente Asesor Titular (Vo.Bo. Oficial)<br>
                  ${tutorTp}<br>
                  Facultad de Derecho
                </div>
              </td>
            </tr>
          </table>
        </body>
        </html>
      `;

      const convertedBlob = asBlob(dossierHtml);
      saveAs(convertedBlob as any, `Expediente_Clinico_SICAJ_${radicado.slice(-6)}.docx`);
      toast.success("Dossier Clínico Unificado (.docx) generado y descargado para archivo y acreditación");
    } catch (err: any) {
      console.error("Error al generar dossier clínico:", err);
      toast.error("Ocurrió un error al compilar el expediente clínico");
    } finally {
      setIsExportingDossier(false);
    }
  };

  const runOralSimulation = () => {
    setIsSimulatingOral(true);
    setTimeout(() => {
      setOralSimulationResult(`
### 🎙️ Cuestionario de Preparación Oral para Audiencia Inicial (Art. 372 CGP)
**Caso:** ${parties} | **Despacho:** ${despacho}

#### 1. Pregunta del Juez sobre Excepciones Previas e Ineptitud:
> *"Señor apoderado del Consultorio Jurídico: La contraparte alega falta de legitimación en la causa por no haberse acreditado el vínculo directo en la pretensión segunda. ¿Cómo desvirtúa usted esa excepción?"*
- **Estrategia Socrática de Respuesta:** "Debe remitirse a la prueba documental No. 3 donde consta el endoso/contrato. Citar que la legitimación procesal se satisface con la titularidad formal del derecho reclamado conforme a la Sentencia SC-1234 de la Corte Suprema."

#### 2. Objeción Probatoria que le planteará la Contraparte:
> *"Objeción al testimonio del señor Restrepo por considerarlo testigo sospechoso (Art. 211 CGP) debido a su relación de parentesco con el demandante."*
- **Técnica de Defensa Oral:** "Alegar que en el CGP no existen testigos inhábiles por parentesco; el juez debe valorar su dicho según las reglas de la sana crítica y corroborar la veracidad con los demás documentos aportados."

#### 3. Estructura recomendada para su Alegato de Conclusión (5 minutos):
- **Minuto 1:** Planteamiento del problema jurídico central.
- **Minuto 2-3:** Demostración de los 3 hechos probados con folios exactos del expediente digital.
- **Minuto 4:** Refutación puntual de las defensas de la contraparte.
- **Minuto 5:** Conclusión con solicitud categórica de condena o amparo.
      `);
      setIsSimulatingOral(false);
      toast.success("Simulacro oral generado por MiA Socrático");
    }, 1200);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] p-0 overflow-hidden rounded-2xl flex flex-col bg-slate-50 border-slate-300">
        {/* ENCABEZADO OFICIAL DE LA CARPETA */}
        <div className="bg-gradient-to-r from-blue-900 via-indigo-950 to-slate-900 text-white p-5 shrink-0">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="p-1 rounded-md bg-blue-500/20 text-blue-300 border border-blue-400/30">
                  <GraduationCap className="h-4 w-4" />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-wider text-blue-200">
                  Carpeta de Caso Clínico & Pedagógico — Ley 2113 de 2021
                </span>
              </div>
              <h2 className="text-base font-bold text-white leading-tight">
                {parties}
              </h2>
              <p className="text-xs text-blue-200/80 flex items-center gap-1.5 mt-0.5">
                <Building2 className="h-3.5 w-3.5 text-blue-400" />
                <span>{despacho}</span>
              </p>
            </div>

            <div className="flex flex-col items-end gap-1.5">
              <Badge className="bg-blue-800/80 text-white border-blue-400/30 text-xs font-mono py-1 px-2.5">
                Rad. {radicado}
              </Badge>
              <div className="flex items-center gap-2 text-[10.5px]">
                <span className="text-emerald-400 font-semibold flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" />
                  Rama Judicial Sincronizada
                </span>
                <Button
                  size="sm"
                  onClick={handleExportExpedienteDossier}
                  disabled={isExportingDossier}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-7 px-2.5 shadow-xs flex items-center gap-1.5"
                >
                  {isExportingDossier ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  <span>Descargar Expediente (.docx)</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Stepper del Flujo de Supervisión Docente */}
          <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-xs">
            <span className="text-blue-200 font-semibold">Estado de Supervisión:</span>
            <div className="flex items-center gap-1.5">
              <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold ${
                supervisionStatus === "draft" ? "bg-amber-400 text-amber-950" : "bg-white/10 text-white/60"
              }`}>1. En Elaboración</span>
              <span className="text-white/40">→</span>
              <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold ${
                supervisionStatus === "review" ? "bg-blue-400 text-blue-950" : "bg-white/10 text-white/60"
              }`}>2. Revisión Tutor</span>
              <span className="text-white/40">→</span>
              <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold ${
                supervisionStatus === "approved" ? "bg-emerald-400 text-emerald-950" : "bg-white/10 text-white/60"
              }`}>3. Visto Bueno</span>
              <span className="text-white/40">→</span>
              <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold ${
                supervisionStatus === "filed" ? "bg-purple-400 text-purple-950" : "bg-white/10 text-white/60"
              }`}>4. Radicado</span>
            </div>
          </div>
        </div>

        {/* TABS DE LA CARPETA CLÍNICA */}
        <Tabs defaultValue="timeline" className="flex-1 flex flex-col overflow-hidden">
          <div className="px-5 pt-3 border-b border-slate-200 bg-white shrink-0">
            <TabsList className="bg-slate-100 p-1 rounded-xl h-auto flex flex-wrap gap-1">
              <TabsTrigger value="timeline" className="text-xs font-bold py-1.5 px-3 data-[state=active]:bg-white data-[state=active]:text-blue-700">
                <Clock className="h-3.5 w-3.5 mr-1.5" />
                <span>Cronología & Explicador Socrático</span>
              </TabsTrigger>
              <TabsTrigger value="supervision" className="text-xs font-bold py-1.5 px-3 data-[state=active]:bg-white data-[state=active]:text-blue-700">
                <Award className="h-3.5 w-3.5 mr-1.5" />
                <span>Supervisión & Rúbrica Ley 2113</span>
              </TabsTrigger>
              <TabsTrigger value="oralidad" className="text-xs font-bold py-1.5 px-3 data-[state=active]:bg-white data-[state=active]:text-blue-700">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                <span>Simulador de Audiencia Oral</span>
              </TabsTrigger>
              <TabsTrigger value="memoriales" className="text-xs font-bold py-1.5 px-3 data-[state=active]:bg-white data-[state=active]:text-blue-700">
                <FileEdit className="h-3.5 w-3.5 mr-1.5" />
                <span>Memoriales Clínicos (1-Clic)</span>
              </TabsTrigger>
              <TabsTrigger value="sicaj" className="text-xs font-bold py-1.5 px-3 data-[state=active]:bg-white data-[state=active]:text-blue-700">
                <UserCheck className="h-3.5 w-3.5 mr-1.5" />
                <span>Ficha Social SICAJ</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto p-5">
            {/* TAB 1: CRONOLOGÍA Y EXPLICADOR SOCRÁTICO */}
            <TabsContent value="timeline" className="mt-0 space-y-4">
              <div className="bg-blue-50/70 border border-blue-200 rounded-xl p-3 text-xs text-blue-900 flex items-start gap-2.5">
                <HelpCircle className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                <p>
                  <strong>Explicador Socrático de Providencias:</strong> Cada actuación procesal emitida por el despacho judicial cuenta con un análisis formativo para que el estudiante comprenda el término legal aplicable y el memorial oportuno que debe proyectar.
                </p>
              </div>

              <div className="space-y-3.5">
                {actuaciones.map((act: any, idx: number) => (
                  <div key={idx} className="bg-white rounded-xl border border-slate-200 p-4 shadow-2xs hover:border-blue-300 transition-all">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-mono font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {act.fecha}
                      </span>
                      <Badge variant="outline" className="text-[10px] text-blue-700 border-blue-200 bg-blue-50">
                        Actuación Judicial Oficial
                      </Badge>
                    </div>

                    <h4 className="text-xs font-bold text-slate-900 mb-1">
                      {act.actuacion}
                    </h4>
                    {act.anotacion && (
                      <p className="text-[11px] text-slate-600 italic mb-3 bg-slate-50 p-2 rounded-lg border border-slate-100">
                        "{act.anotacion}"
                      </p>
                    )}

                    {/* TARJETA PEDAGÓGICA SOCRÁTICA */}
                    {act.pedagogia ? (
                      <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-3 text-xs space-y-2 mt-2">
                        <div className="flex items-center gap-1.5 font-bold text-emerald-900 text-[11px]">
                          <BookOpen className="h-3.5 w-3.5 text-emerald-700" />
                          <span>¿Qué significa esta providencia para el estudiante?</span>
                        </div>
                        <p className="text-slate-700 text-[11.5px] leading-relaxed">
                          {act.pedagogia.significado}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 border-t border-emerald-200/60 text-[11px]">
                          <div>
                            <span className="font-semibold text-slate-900 block">Fundamento Legal:</span>
                            <span className="text-slate-600">{act.pedagogia.norma}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-slate-900 block">Término Judicial:</span>
                            <span className="text-amber-700 font-bold">{act.pedagogia.termino}</span>
                          </div>
                        </div>
                        <div className="pt-1.5 border-t border-emerald-200/60 flex items-center justify-between">
                          <span className="text-[11px] text-emerald-950 font-medium">
                            <strong>Acción recomendada:</strong> {act.pedagogia.accionEstudiante}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-[11px] text-slate-600">
                        Providencia en trámite de análisis pedagógico por el tutor.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* TAB 2: SUPERVISIÓN Y RÚBRICA LEY 2113 */}
            <TabsContent value="supervision" className="mt-0 space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4 shadow-2xs">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div>
                    <h3 className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <Award className="h-4 w-4 text-indigo-600" />
                      <span>Flujo de Aprobación Docente (Ley 2113 de 2021)</span>
                    </h3>
                    <p className="text-[11px] text-slate-500">
                      {activeRole === "estudiante"
                        ? "Como estudiante practicante, debes remitir tus proyectos a revisión antes de cualquier actuación."
                        : "Como docente asesor, evalúa el memorial y otorga el visto bueno obligatorio antes de la radicación judicial."}
                    </p>
                  </div>
                  
                  {/* Conmutador de Rol para Pruebas / Demostración */}
                  <div className="flex items-center gap-2">
                    <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[10.5px]">
                      <button
                        type="button"
                        onClick={() => setActiveRole("estudiante")}
                        className={`px-2.5 py-1 rounded-md font-bold transition-all ${
                          activeRole === "estudiante" ? "bg-white text-blue-700 shadow-2xs" : "text-slate-500 hover:text-slate-800"
                        }`}
                      >
                        Perfil: Estudiante
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveRole("docente")}
                        className={`px-2.5 py-1 rounded-md font-bold transition-all ${
                          activeRole === "docente" ? "bg-white text-indigo-700 shadow-2xs" : "text-slate-500 hover:text-slate-800"
                        }`}
                      >
                        Perfil: Docente Tutor
                      </button>
                    </div>
                  </div>
                </div>

                {/* Botonera de Acciones Restringida por Rol */}
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <div className="text-xs">
                    <span className="font-bold text-slate-800 block">
                      {activeRole === "estudiante" ? "Acción del Estudiante Practicante:" : "Potestad Docente Asesor (Ley 2113):"}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {activeRole === "estudiante"
                        ? "El estudiante no puede otorgar visto bueno. Solo remite a revisión."
                        : "Revisa la técnica procesal y decide si otorgas Vo.Bo. oficial o solicitas ajustes."}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    {activeRole === "estudiante" ? (
                      <Button
                        size="sm"
                        disabled={supervisionStatus === "review" || supervisionStatus === "approved"}
                        onClick={() => setIsEmailConfirmOpen(true)}
                        className="text-xs h-8 bg-blue-600 hover:bg-blue-700 text-white font-bold"
                      >
                        <Send className="h-3.5 w-3.5 mr-1.5" />
                        <span>
                          {supervisionStatus === "review"
                            ? "En Espera de Revisión Docente..."
                            : supervisionStatus === "approved"
                            ? "Aprobado por el Tutor (Bloqueado)"
                            : "Solicitar Revisión al Docente Tutor"}
                        </span>
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isSubmittingDecision}
                          onClick={() => handleDecisionDocente("devuelto")}
                          className="text-xs h-8 text-amber-700 border-amber-300 hover:bg-amber-50 font-bold"
                        >
                          {isSubmittingDecision ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                          <span>Devolver para Ajustes</span>
                        </Button>
                        <Button
                          size="sm"
                          disabled={isSubmittingDecision || supervisionStatus === "approved"}
                          onClick={() => {
                            setSigningRole("docente");
                            setIsSignModalOpen(true);
                          }}
                          className="text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                        >
                          {isSubmittingDecision ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                          ) : (
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          <span>
                            {supervisionStatus === "approved"
                              ? "Vo.Bo. Otorgado (Firma Acreditada)"
                              : "Firmar y Otorgar Visto Bueno"}
                          </span>
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* ESTADO DE FIRMAS FORMALES LEY 2113 DE 2021 */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-3">
                  <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                    <span className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                      <PenTool className="h-4 w-4 text-indigo-600" />
                      <span>Suscripción Formal de Documentos (Ley 2113 Art. 9 & Ley 527 de 1999)</span>
                    </span>
                    <Badge variant="outline" className="text-[10px] bg-white text-slate-700">
                      Doble Firma Obligatoria
                    </Badge>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    {/* Firma Estudiante */}
                    <div className="bg-white p-3 rounded-lg border border-slate-200 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-slate-800">Firma Estudiante Practicante</span>
                          {studentSignature ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[9.5px]">✓ Firmado</Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-700 border-amber-300 text-[9.5px]">Pendiente</Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 mb-2">{studentName} — {studentDocId}</p>
                        {studentSignature && (
                          <div className="border border-slate-200 rounded p-1 mb-2 bg-slate-50/50 flex justify-center">
                            <img src={studentSignature} alt="Firma Estudiante" className="h-10 object-contain" />
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSigningRole("estudiante");
                          setIsSignModalOpen(true);
                        }}
                        className="h-7 text-xs font-bold w-full"
                      >
                        <PenTool className="h-3 w-3 mr-1" />
                        <span>{studentSignature ? "Actualizar Firma Practicante" : "Capturar Firma Practicante"}</span>
                      </Button>
                    </div>

                    {/* Firma Docente */}
                    <div className="bg-white p-3 rounded-lg border border-slate-200 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-slate-800">Firma Docente Asesor (Vo.Bo.)</span>
                          {tutorSignature || supervisionStatus === "approved" ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[9.5px]">✓ Vo.Bo. Acreditado</Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-700 border-amber-300 text-[9.5px]">Sin Visto Bueno</Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 mb-2">{tutorName} — {tutorTp}</p>
                        {tutorSignature && (
                          <div className="border border-slate-200 rounded p-1 mb-2 bg-slate-50/50 flex justify-center">
                            <img src={tutorSignature} alt="Firma Docente" className="h-10 object-contain" />
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSigningRole("docente");
                          setIsSignModalOpen(true);
                        }}
                        className="h-7 text-xs font-bold w-full text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                      >
                        <PenTool className="h-3 w-3 mr-1" />
                        <span>{tutorSignature ? "Actualizar Firma Docente" : "Firmar y Otorgar Vo.Bo."}</span>
                      </Button>
                    </div>
                  </div>
                </div>

                {/* VISOR FORENSE DEL MEMORIAL PROYECTADO POR EL PRACTICANTE */}
                {(proceso?.borrador_juridico || proceso?.metadata?.borrador_juridico) && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                        <FileText className="h-4 w-4 text-indigo-600" />
                        <span>Memorial Judicial Proyectado por el Practicante para Vo.Bo.:</span>
                      </span>
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCopyText(proceso?.borrador_juridico || proceso?.metadata?.borrador_juridico, "draft_copy")}
                          className="h-6 text-[10.5px] px-2 text-slate-600 bg-white"
                        >
                          {copiedId === "draft_copy" ? <Check className="h-3 w-3 text-emerald-600 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                          <span>{copiedId === "draft_copy" ? "Copiado" : "Copiar"}</span>
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleDownloadWordMemorial(
                            `Memorial_${radicado.slice(-6)}`,
                            `<p>${(proceso?.borrador_juridico || proceso?.metadata?.borrador_juridico || "").replace(/\n/g, "<br>")}</p>`
                          )}
                          className="h-6 text-[10.5px] px-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          <span>Word (.docx)</span>
                        </Button>
                        <Badge className="bg-amber-100 text-amber-900 border-amber-300 text-[10px]">
                          Sujeto a Vo.Bo.
                        </Badge>
                      </div>
                    </div>
                    <div className="p-3 bg-white border border-slate-200 rounded-lg max-h-56 overflow-y-auto text-xs text-slate-700 font-mono whitespace-pre-wrap leading-relaxed custom-scrollbar">
                      {proceso?.borrador_juridico || proceso?.metadata?.borrador_juridico}
                    </div>
                  </div>
                )}

                {/* BANNER DE DOCUMENTOS OBLIGATORIOS EXIGIDOS POR LA FACULTAD & RESOLUCIÓN DE DUDAS */}
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/90 flex flex-col gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      Documentos Reglamentarios Exigidos por la Facultad (Ley 2113 de 2021)
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setIsDoubtModalOpen(true)}
                      className="h-6 text-[10.5px] px-2 text-indigo-700 border-indigo-300 bg-indigo-50/70 hover:bg-indigo-100 flex items-center gap-1 font-semibold cursor-pointer"
                    >
                      <CalendarPlus className="h-3 w-3 text-indigo-600" />
                      <span>¿Dudas con el trámite? Pedir cita con profesor/asistente</span>
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[10.5px]">
                    <div className="p-1.5 rounded-lg bg-white border border-slate-200 font-medium text-slate-700 flex items-center gap-1 shadow-2xs">
                      <span className="text-emerald-600 font-bold">✓</span> 1. Ficha Entrevista
                    </div>
                    <div className="p-1.5 rounded-lg bg-white border border-slate-200 font-medium text-slate-700 flex items-center gap-1 shadow-2xs">
                      <span className="text-emerald-600 font-bold">✓</span> 2. Matriz Probatoria
                    </div>
                    <div className="p-1.5 rounded-lg bg-white border border-slate-200 font-medium text-slate-700 flex items-center gap-1 shadow-2xs">
                      <span className="text-emerald-600 font-bold">✓</span> 3. Dictamen Viabilidad
                    </div>
                    <div className="p-1.5 rounded-lg bg-white border border-slate-200 font-medium text-slate-700 flex items-center gap-1 shadow-2xs">
                      <span className="text-emerald-600 font-bold">✓</span> 4. Borrador Judicial
                    </div>
                    <div className="p-1.5 rounded-lg bg-white border border-slate-200 font-medium text-slate-700 flex items-center gap-1 shadow-2xs">
                      <span className="text-emerald-600 font-bold">✓</span> 5. Poder / Asignación
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="font-bold text-slate-700 block mb-1">Docente Asesor / Tutor Responsable:</label>
                    <Input
                      value={tutorName}
                      onChange={(e) => setTutorName(e.target.value)}
                      className="text-xs h-8"
                    />
                  </div>
                  <div>
                    <label className="font-bold text-slate-700 block mb-1">Estudiante Practicante Asignado:</label>
                    <Input
                      value={studentName}
                      onChange={(e) => setStudentName(e.target.value)}
                      className="text-xs h-8"
                    />
                  </div>
                </div>

                {/* Rúbrica Pedagógica Interactiva Ley 2113 de 2021 */}
                <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200 space-y-2.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900 block text-[11px] uppercase tracking-wide">
                      Rúbrica de Evaluación de Práctica Jurídica (Ley 2113 de 2021):
                    </span>
                    <Badge variant="outline" className="text-[10px] font-mono text-indigo-700 bg-white">
                      Promedio: {((rubricaTecnica + rubricaCitas + rubricaRedaccion) / 3).toFixed(1)} / 5.0
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-[11px]">
                    <div className="bg-white p-2.5 rounded-lg border border-slate-200 shadow-2xs">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-bold text-slate-700">Técnica Procesal:</span>
                        <span className="font-black text-indigo-700">{rubricaTecnica.toFixed(1)}</span>
                      </div>
                      {activeRole === "docente" ? (
                        <input
                          type="range"
                          min="1.0"
                          max="5.0"
                          step="0.1"
                          value={rubricaTecnica}
                          onChange={(e) => setRubricaTecnica(parseFloat(e.target.value))}
                          className="w-full accent-indigo-600 cursor-pointer h-1.5"
                        />
                      ) : (
                        <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                          <div className="bg-emerald-600 h-full" style={{ width: `${(rubricaTecnica / 5) * 100}%` }} />
                        </div>
                      )}
                    </div>

                    <div className="bg-white p-2.5 rounded-lg border border-slate-200 shadow-2xs">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-bold text-slate-700">Citas & Relatoría:</span>
                        <span className="font-black text-indigo-700">{rubricaCitas.toFixed(1)}</span>
                      </div>
                      {activeRole === "docente" ? (
                        <input
                          type="range"
                          min="1.0"
                          max="5.0"
                          step="0.1"
                          value={rubricaCitas}
                          onChange={(e) => setRubricaCitas(parseFloat(e.target.value))}
                          className="w-full accent-indigo-600 cursor-pointer h-1.5"
                        />
                      ) : (
                        <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                          <div className="bg-emerald-600 h-full" style={{ width: `${(rubricaCitas / 5) * 100}%` }} />
                        </div>
                      )}
                    </div>

                    <div className="bg-white p-2.5 rounded-lg border border-slate-200 shadow-2xs">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-bold text-slate-700">Redacción Forense:</span>
                        <span className="font-black text-indigo-700">{rubricaRedaccion.toFixed(1)}</span>
                      </div>
                      {activeRole === "docente" ? (
                        <input
                          type="range"
                          min="1.0"
                          max="5.0"
                          step="0.1"
                          value={rubricaRedaccion}
                          onChange={(e) => setRubricaRedaccion(parseFloat(e.target.value))}
                          className="w-full accent-indigo-600 cursor-pointer h-1.5"
                        />
                      ) : (
                        <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                          <div className="bg-emerald-600 h-full" style={{ width: `${(rubricaRedaccion / 5) * 100}%` }} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="font-bold text-slate-700 text-xs">
                      Comentarios Pedagógicos del Docente Tutor para el Estudiante:
                    </label>
                    {activeRole === "estudiante" && (
                      <Badge variant="outline" className="text-[9.5px] bg-slate-100 text-slate-600">
                        Solo Lectura (Evaluación Oficial)
                      </Badge>
                    )}
                  </div>
                  <Textarea
                    value={tutorFeedback}
                    onChange={(e) => setTutorFeedback(e.target.value)}
                    rows={3}
                    readOnly={activeRole === "estudiante"}
                    className={`text-xs ${activeRole === "estudiante" ? "bg-slate-50 cursor-default text-slate-700" : ""}`}
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    Esta retroalimentación queda registrada en la bitácora del estudiante para su nota de semestre.
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* TAB 3: SIMULADOR DE AUDIENCIA ORAL */}
            <TabsContent value="oralidad" className="mt-0 space-y-4">
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-indigo-600" />
                    <div>
                      <h3 className="text-xs font-bold text-indigo-950">
                        Entrenamiento de Audiencia Oral con MiA Socrático
                      </h3>
                      <p className="text-[11px] text-indigo-800">
                        Prepara al estudiante para la audiencia real (Art. 372 CGP / CPTSS) simulando preguntas punzantes del juez y objeciones de la contraparte.
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={runOralSimulation}
                    disabled={isSimulatingOral}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs"
                  >
                    {isSimulatingOral ? "Generando Careo..." : "Iniciar Simulación Oral"}
                  </Button>
                </div>
              </div>

              {oralSimulationResult && (
                <div className="bg-white rounded-xl border border-slate-200 p-4 text-xs space-y-3 shadow-2xs">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <span className="font-bold text-slate-900 flex items-center gap-1.5 text-xs">
                      <GraduationCap className="h-4 w-4 text-blue-600" />
                      <span>Guía de Audiencia y Preguntas Clave del Despacho</span>
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCopyText(oralSimulationResult, "oral")}
                      className="text-[11px] h-7"
                    >
                      {copiedId === "oral" ? <Check className="h-3 w-3 text-emerald-600 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                      <span>Copiar Guía</span>
                    </Button>
                  </div>
                  <div className="text-slate-700 whitespace-pre-line leading-relaxed text-[11.5px]">
                    {oralSimulationResult}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* TAB 4: MEMORIALES CLÍNICOS EN 1-CLIC */}
            <TabsContent value="memoriales" className="mt-0 space-y-4">
              <div className="text-xs text-slate-600 mb-2">
                Genera en 1-clic memoriales comunes de consultorio adaptados a los datos y actuaciones reales de este caso judicial:
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {/* Memorial 1 */}
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 hover:border-blue-300 transition-all flex flex-col justify-between">
                  <div>
                    <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] mb-1.5">
                      Art. 121 CGP
                    </Badge>
                    <h4 className="text-xs font-bold text-slate-900 mb-1">
                      Impulso Procesal por Mora Judicial
                    </h4>
                    <p className="text-[11px] text-slate-500 mb-3">
                      Solicita al despacho emitir la providencia pendiente recordando los términos legales perentorios.
                    </p>
                  </div>
                  <div className="flex gap-2 pt-2 border-t border-slate-100">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopyText(
                        `SEÑOR JUEZ ${despacho.toUpperCase()}\nREF: RAD. ${radicado}\nASUNTO: IMPULSO PROCESAL ART. 121 CGP...\n\nPor medio del presente memorial, ${studentName}, en calidad de apoderado del Consultorio Jurídico, respetuosamente solicito se sirva impulsar la actuación procesal correspondiente...`,
                        "m1"
                      )}
                      className="text-xs h-7 flex-1"
                    >
                      <Copy className="h-3 w-3 mr-1" /> Copiar
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleDownloadWordMemorial(
                        "Memorial_Impulso_Procesal",
                        `<p><strong>SEÑOR JUEZ</strong><br><strong>${despacho.toUpperCase()}</strong></p><p><strong>REF:</strong> PROCESO ${parties}<br><strong>RADICADO:</strong> ${radicado}</p><p><strong>ASUNTO: SOLICITUD DE IMPULSO PROCESAL (ARTÍCULO 121 DEL CGP)</strong></p><p>${studentName}, identificado como aparece al pie de mi firma, en mi calidad de estudiante practicante del Consultorio Jurídico debidamente autorizado por el docente tutor, respetuosamente manifiesto que el expediente se encuentra pendiente de emitir pronunciamiento de fondo, por lo cual solicito muy cordialmente se sirva dar trámite preferente a la siguiente etapa procesal.</p>`
                      )}
                      className="text-xs h-7 bg-blue-600 hover:bg-blue-700 text-white flex-1"
                    >
                      <Download className="h-3 w-3 mr-1" /> Descargar Word
                    </Button>
                  </div>
                </div>

                {/* Memorial 2 */}
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 hover:border-blue-300 transition-all flex flex-col justify-between">
                  <div>
                    <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] mb-1.5">
                      Ley 2213 de 2022
                    </Badge>
                    <h4 className="text-xs font-bold text-slate-900 mb-1">
                      Constancia de Notificación Electrónica
                    </h4>
                    <p className="text-[11px] text-slate-500 mb-3">
                      Aporta el acuse de recibo del correo electrónico enviado a la contraparte conforme al Art. 8.
                    </p>
                  </div>
                  <div className="flex gap-2 pt-2 border-t border-slate-100">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopyText(
                        `SEÑOR JUEZ ${despacho.toUpperCase()}\nREF: RAD. ${radicado}\nASUNTO: APORTA CONSTANCIA DE NOTIFICACIÓN ELECTRÓNICA LEY 2213 DE 2022...`,
                        "m2"
                      )}
                      className="text-xs h-7 flex-1"
                    >
                      <Copy className="h-3 w-3 mr-1" /> Copiar
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleDownloadWordMemorial(
                        "Memorial_Notificacion_Electronica",
                        `<p><strong>SEÑOR JUEZ</strong><br><strong>${despacho.toUpperCase()}</strong></p><p><strong>REF:</strong> ${parties}<br><strong>RADICADO:</strong> ${radicado}</p><p><strong>ASUNTO: APORTE DE NOTIFICACIÓN ELECTRÓNICA CONFORME AL ARTÍCULO 8 DE LA LEY 2213 DE 2022</strong></p><p>${studentName}, en mi condición de apoderado adscrito al Consultorio Jurídico, me permito allegar al plenario la constancia de envío del traslado de la demanda y sus anexos a la dirección electrónica de la contraparte, solicitando se declare surtida la notificación personal.</p>`
                      )}
                      className="text-xs h-7 bg-blue-600 hover:bg-blue-700 text-white flex-1"
                    >
                      <Download className="h-3 w-3 mr-1" /> Descargar Word
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* TAB 5: FICHA SOCIAL SICAJ (MINJUSTICIA) */}
            <TabsContent value="sicaj" className="mt-0 space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3.5 shadow-2xs">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                  <div>
                    <h3 className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <UserCheck className="h-4 w-4 text-emerald-600" />
                      <span>Ficha de Impacto Social para Reporte SICAJ (MinJusticia)</span>
                    </h3>
                    <p className="text-[11px] text-slate-500">
                      Datos consolidados para el informe anual obligatorio de consultorios jurídicos.
                    </p>
                  </div>
                  <Badge className="bg-emerald-50 text-emerald-800 border-emerald-300 text-[10px]">
                    Ley 2113 de 2021
                  </Badge>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                    <span className="font-semibold text-slate-500 block text-[10px]">USUARIO BENEFICIARIO:</span>
                    <span className="font-bold text-slate-800 text-xs">Graciela López Gil</span>
                  </div>
                  <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                    <span className="font-semibold text-slate-500 block text-[10px]">ESTRATO SOCIOECONÓMICO:</span>
                    <span className="font-bold text-slate-800 text-xs">Estrato 1 (Gratuidad Legal)</span>
                  </div>
                  <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                    <span className="font-semibold text-slate-500 block text-[10px]">POBLACIÓN ESPECIAL PROTECCIÓN:</span>
                    <span className="font-bold text-slate-800 text-xs">Adulto Mayor / Sujeto Constitucional</span>
                  </div>
                  <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                    <span className="font-semibold text-slate-500 block text-[10px]">HORAS DE PRÁCTICA ACREDITADAS:</span>
                    <span className="font-bold text-emerald-700 text-xs">14 Horas Certificables</span>
                  </div>
                </div>

                <div className="pt-2 flex justify-end">
                  <Button
                    size="sm"
                    onClick={handleExportExpedienteDossier}
                    disabled={isExportingDossier}
                    className="bg-slate-900 hover:bg-slate-800 text-white text-xs"
                  >
                    {isExportingDossier ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                    <span>Exportar Expediente Certificado (.docx)</span>
                  </Button>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>

        {/* DIÁLOGO MODAL: REMITIR A REVISIÓN CON NOTIFICACIÓN POR CORREO */}
        <Dialog open={isEmailConfirmOpen} onOpenChange={setIsEmailConfirmOpen}>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-600" />
                <span>Remitir Memorial para Revisión y Visto Bueno</span>
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Se enviará una notificación por correo electrónico al docente asesor con un enlace directo a su vista de supervisión para evaluar este caso.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-2 text-xs">
              <div>
                <label className="font-bold text-slate-700 block mb-1">Docente Asesor Responsable:</label>
                <Input
                  value={tutorName}
                  onChange={(e) => setTutorName(e.target.value)}
                  className="text-xs h-8"
                />
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Correo Electrónico del Docente:</label>
                <Input
                  type="email"
                  placeholder="ejemplo@universidad.edu.co"
                  value={tutorEmail}
                  onChange={(e) => setTutorEmail(e.target.value)}
                  className="text-xs h-8 font-mono"
                />
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">
                  Nota o Resumen del Estudiante sobre el Memorial Proyectado:
                </label>
                <Textarea
                  placeholder="Estimada docente: Remito el proyecto de impulso procesal conforme al Art. 121 del CGP. Adjunto folios del informe rendido."
                  value={studentNotes}
                  onChange={(e) => setStudentNotes(e.target.value)}
                  rows={3}
                  className="text-xs"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-2.5 text-[11px] text-blue-900">
                <strong>Enlace de acceso docente incluido:</strong> El correo contendrá un enlace seguro con parámetro <code className="bg-white px-1 py-0.5 rounded text-blue-800">?view=docente</code> que abrirá automáticamente el caso en la perspectiva del tutor.
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsEmailConfirmOpen(false)}
                className="text-xs"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={isSendingReview || !tutorEmail.trim()}
                onClick={handleSendReviewEmail}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs"
              >
                {isSendingReview ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : (
                  <Send className="h-3.5 w-3.5 mr-1.5" />
                )}
                <span>Enviar Notificación al Docente</span>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* DIÁLOGO MODAL: CAPTURA DE FIRMA DIGITAL Y MANUSCRITA (LEY 2113 Y LEY 527) */}
        <Dialog open={isSignModalOpen} onOpenChange={setIsSignModalOpen}>
          <DialogContent className="sm:max-w-lg rounded-2xl p-5 bg-white">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <span className="p-1 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200">
                  <PenTool className="h-4 w-4" />
                </span>
                <DialogTitle className="text-sm font-bold text-slate-900">
                  {signingRole === "docente"
                    ? "Suscripción y Visto Bueno Oficial Docente (Ley 2113)"
                    : "Suscripción de Memorial — Estudiante Practicante"}
                </DialogTitle>
              </div>
              <DialogDescription className="text-xs text-slate-500">
                {signingRole === "docente"
                  ? "Su firma y número de Tarjeta Profesional quedarán incrustados en el expediente clínico y memorial judicial conforme a la Ley 2113 de 2021 y Ley 527 de 1999."
                  : "Capture su firma autógrafa para validar su autoría y radicación en el Consultorio Jurídico."}
              </DialogDescription>
            </DialogHeader>

            {/* Selector de Modo de Firma */}
            <div className="flex bg-slate-100 p-1 rounded-xl text-xs gap-1 my-2">
              <button
                type="button"
                onClick={() => setSignatureMode("canvas")}
                className={`flex-1 py-1.5 rounded-lg font-bold transition-all ${
                  signatureMode === "canvas" ? "bg-white text-indigo-700 shadow-2xs" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Firma Manuscrita (Canvas)
              </button>
              <button
                type="button"
                onClick={() => setSignatureMode("stamp")}
                className={`flex-1 py-1.5 rounded-lg font-bold transition-all ${
                  signatureMode === "stamp" ? "bg-white text-indigo-700 shadow-2xs" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Sello Criptográfico Ley 527
              </button>
            </div>

            {signatureMode === "canvas" ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[11px] text-slate-500">
                  <span>Dibuje su firma con el mouse o pantalla táctil:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px]">Tinta:</span>
                    <button
                      type="button"
                      onClick={() => setPenColor("#000000")}
                      className={`w-4 h-4 rounded-full bg-black ${penColor === "#000000" ? "ring-2 ring-indigo-500" : ""}`}
                    />
                    <button
                      type="button"
                      onClick={() => setPenColor("#063970")}
                      className={`w-4 h-4 rounded-full bg-[#063970] ${penColor === "#063970" ? "ring-2 ring-indigo-500" : ""}`}
                    />
                    <button
                      type="button"
                      onClick={() => signaturePadInstance.current?.clear()}
                      className="text-xs text-rose-600 hover:underline font-bold ml-1"
                    >
                      Limpiar
                    </button>
                  </div>
                </div>

                <div className="border border-slate-300 rounded-xl overflow-hidden bg-white shadow-inner h-40 relative">
                  <canvas
                    ref={signaturePadCanvasRef}
                    className="w-full h-full cursor-crosshair touch-none"
                  />
                  <div className="absolute bottom-1 right-2 pointer-events-none text-[9.5px] text-slate-400 font-mono">
                    Área de Suscripción Oficial
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-xl border border-indigo-200 bg-indigo-50/50 space-y-2 text-xs">
                <div className="flex items-center gap-2 text-indigo-950 font-bold">
                  <FileCheck className="h-4 w-4 text-emerald-600" />
                  <span>Sello Digital de Integridad Probatoria</span>
                </div>
                <div className="text-[11px] text-slate-600 space-y-1">
                  <p><strong>Firmante:</strong> {signingRole === "docente" ? tutorName : studentName}</p>
                  <p><strong>Identificación:</strong> {signingRole === "docente" ? tutorTp : studentDocId}</p>
                  <p><strong>Normativa de Validez:</strong> Ley 527 de 1999 & Decreto 2364 de 2012</p>
                  <p className="font-mono text-[9.5px] text-slate-500">
                    Estampa de Tiempo: {new Date().toISOString().replace("T", " ").slice(0, 19)} UTC
                  </p>
                </div>
              </div>
            )}

            {/* Inputs de Identificación Formal */}
            <div className="grid grid-cols-2 gap-2.5 pt-2 text-xs">
              <div>
                <label className="font-bold text-slate-700 block text-[10.5px] mb-0.5">
                  {signingRole === "docente" ? "Tarjeta Profesional C.S.J.:" : "Documento de Identidad:"}
                </label>
                <Input
                  value={signingRole === "docente" ? tutorTp : studentDocId}
                  onChange={(e) => signingRole === "docente" ? setTutorTp(e.target.value) : setStudentDocId(e.target.value)}
                  className="h-7 text-xs font-mono"
                />
              </div>
              <div>
                <label className="font-bold text-slate-700 block text-[10.5px] mb-0.5">Firmante Titular:</label>
                <Input
                  value={signingRole === "docente" ? tutorName : studentName}
                  disabled
                  className="h-7 text-xs bg-slate-50 text-slate-700"
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsSignModalOpen(false)}
                className="text-xs"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleCaptureDone}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                <span>
                  {signingRole === "docente"
                    ? "Firmar y Otorgar Visto Bueno (+4 hrs)"
                    : "Confirmar Firma del Practicante"}
                </span>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* DIÁLOGO MODAL: SOLICITAR CITA DE ASESORÍA / RESOLVER DUDAS (FACULTAD DE DERECHO) */}
        <Dialog open={isDoubtModalOpen} onOpenChange={setIsDoubtModalOpen}>
          <DialogContent className="sm:max-w-md bg-white rounded-2xl p-5">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <span className="p-1.5 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200">
                  <CalendarPlus className="h-4 w-4" />
                </span>
                <DialogTitle className="text-base font-bold text-slate-900">
                  Solicitar Cita de Asesoría para este Expediente
                </DialogTitle>
              </div>
              <DialogDescription className="text-xs text-slate-500">
                Radicado: <span className="font-mono font-bold text-slate-700">{radicado}</span>. Formula tus dudas técnicas o procesales con el profesor del área o asistente.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3.5 py-2">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Destinatario de la consulta:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setDoubtRecipient("docente")}
                    className={`p-2.5 rounded-xl border text-left transition-all text-xs ${
                      doubtRecipient === "docente"
                        ? "border-indigo-600 bg-indigo-50/70 text-indigo-950 font-bold shadow-2xs"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <span className="block font-bold">Docente Asesor</span>
                    <span className="text-[10px] text-slate-500">{tutorName}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDoubtRecipient("asistente")}
                    className={`p-2.5 rounded-xl border text-left transition-all text-xs ${
                      doubtRecipient === "asistente"
                        ? "border-indigo-600 bg-indigo-50/70 text-indigo-950 font-bold shadow-2xs"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <span className="block font-bold">Asistente de Sala</span>
                    <span className="text-[10px] text-slate-500">Apoyo operativo de consultorio</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Fecha Sugerida para la Cita:</label>
                <Input
                  type="date"
                  value={doubtDate}
                  onChange={(e) => setDoubtDate(e.target.value)}
                  className="text-xs h-8 border-slate-200 bg-white"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Describe puntualmente tus dudas o el obstáculo procesal:
                </label>
                <Textarea
                  placeholder="Ej: Tengo duda de si debo aportar el registro civil de nacimiento original o si es suficiente con copia simple según el Art. 244 del CGP..."
                  value={doubtText}
                  onChange={(e) => setDoubtText(e.target.value)}
                  rows={4}
                  className="text-xs resize-none border-slate-200"
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsDoubtModalOpen(false)}
                className="text-xs"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={isSavingDoubt || !doubtText.trim()}
                onClick={() => {
                  setIsSavingDoubt(true);
                  try {
                    const newAppointment = {
                      id: `cita-${Date.now()}`,
                      case_id: radicado,
                      case_title: parties,
                      recipient: doubtRecipient,
                      recipient_name: doubtRecipient === "docente" ? tutorName : "Asistente de Consultorio",
                      student_name: studentName,
                      doubt: doubtText.trim(),
                      date: doubtDate || new Date().toISOString().split("T")[0],
                      status: "pendiente",
                      created_at: new Date().toISOString()
                    };
                    const stored = localStorage.getItem("campus_appointments_v1");
                    const list = stored ? JSON.parse(stored) : [];
                    localStorage.setItem("campus_appointments_v1", JSON.stringify([newAppointment, ...list]));

                    toast.success(
                      doubtRecipient === "docente"
                        ? `Cita de asesoría agendada con ${tutorName}`
                        : "Cita agendada con el Asistente de Consultorio"
                    );
                    setIsDoubtModalOpen(false);
                    setDoubtText("");
                  } catch (e) {
                    toast.error("Error al registrar la cita");
                  } finally {
                    setIsSavingDoubt(false);
                  }
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs"
              >
                {isSavingDoubt ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CalendarPlus className="h-3.5 w-3.5 mr-1" />}
                <span>Solicitar Asesoría</span>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

